/** Polarity / channel / tenant controls for a level=tag flag row.
 *
 * Shared by the create and edit dialogs so the two cannot describe the same
 * three fields differently — they are the ones an operator is most likely to
 * get wrong, and both surfaces have to say the same thing about them.
 *
 * The tenant picker has no System sentinel here: an EMPTY tenant IS the system
 * row. (Contrast the alias and canonical grids, where a filter needs to say
 * "system only" and therefore needs the sentinel.)
 */
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';

import { FLAG_CHANNELS, type FlagChannel, type FlagPolarity } from '@/api/notificationFeatures';
import { FieldHelp } from '@/components/FieldHelp';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { flagTenantActions } from './featureFlagLinks';
import { FLAG_HELP } from './fieldHelp';

const CHANNEL_LABELS: Record<FlagChannel, string> = {
  '': 'Every channel',
  email: 'Email',
  sms: 'SMS',
  chat: 'Chat',
  whatsapp: 'WhatsApp',
  airbnb: 'Airbnb',
};

interface Props {
  polarity: FlagPolarity;
  onPolarityChange: (v: FlagPolarity) => void;
  channel: FlagChannel;
  onChannelChange: (v: FlagChannel) => void;
  tenant: TenantPickerValue | null;
  onTenantChange: (v: TenantPickerValue | null) => void;
  /** Scope is immutable after create — the edit dialog renders it read-only. */
  scopeLocked?: boolean;
  /** Chips currently configured; an exclude with none suppresses everything. */
  chipCount: number;
}

export function TagFlagScopeFields({
  polarity,
  onPolarityChange,
  channel,
  onChannelChange,
  tenant,
  onTenantChange,
  scopeLocked = false,
  chipCount,
}: Props) {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          select
          size="small"
          fullWidth
          label="Polarity"
          value={polarity}
          onChange={(e) => onPolarityChange(e.target.value as FlagPolarity)}
        >
          <MenuItem value="require">Require — render only when the tags match</MenuItem>
          <MenuItem value="exclude">Exclude — render only when they do NOT</MenuItem>
        </TextField>
        <FieldHelp help={FLAG_HELP.polarity} />
      </Box>

      {polarity === 'exclude' && chipCount === 0 && (
        <Alert severity="error">
          An exclude condition with no tags would suppress this feature for every booking. Add at
          least one tag.
        </Alert>
      )}

      {/* A system-scoped exclude is perfectly valid and silently enormous: it
          suppresses the feature for every operator on the platform, not just the
          one whose policy prompted it. Distinct from the chip-less case above —
          that one is refused, this one is allowed and has to be deliberate. */}
      {polarity === 'exclude' && chipCount > 0 && tenant === null && (
        <Alert severity="warning">
          This exclude has <strong>no operator</strong>, so it applies to{' '}
          <strong>every operator on the platform</strong> — any booking carrying these tags loses
          the feature, at every tenant. Set an operator to scope it to one.
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          select
          size="small"
          fullWidth
          label="Channel"
          value={channel}
          onChange={(e) => onChannelChange(e.target.value as FlagChannel)}
          helperText={
            channel === ''
              ? 'Applies to every render, including the canonical base render kept for audit.'
              : `Applies only to ${CHANNEL_LABELS[channel]} deliveries. The base render is unaffected.`
          }
        >
          {FLAG_CHANNELS.map((c) => (
            <MenuItem key={c || 'any'} value={c}>
              {CHANNEL_LABELS[c]}
            </MenuItem>
          ))}
        </TextField>
        <FieldHelp help={FLAG_HELP.channel} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          {scopeLocked ? (
            // Read-only text rather than a disabled picker: the scope is
            // immutable after create, and a greyed-out picker invites the click
            // that cannot work.
            <TextField
              size="small"
              fullWidth
              label="Operator"
              value={tenant ? (tenant.code ?? tenant.name) : 'All operators (system-wide)'}
              InputProps={{ readOnly: true }}
              helperText="Scope is fixed at creation. Create a separate row for another operator."
            />
          ) : (
            <TenantPicker
              value={tenant}
              onChange={onTenantChange}
              label="Operator"
              placeholder="All operators (system-wide)"
            />
          )}
          {/* Read-only or picked, the operator itself is worth reaching: its
              page, and its other flag rows. The picker keeps its own click. */}
          {tenant && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              <ValueActions actions={flagTenantActions(tenant)}>
                {tenant.code ?? tenant.name}
              </ValueActions>
            </Typography>
          )}
        </Box>
        <FieldHelp help={FLAG_HELP.tag_tenant} />
      </Box>
    </>
  );
}
